import { documentTypeId } from 'Utils/constants'
import { hasPendingOrEmptyPaymentMethod } from 'Views/OrdenDeVenta/utils/paymentMethodSelection'
import { isWebDocumentOrigin } from 'Utils/isWebDocumentOrigin'

// Only a web quote can have a web sales order behind it, so quotes from any
// other origin never query the related order.
export const getRelatedWebSalesOrder = quote =>
  isWebDocumentOrigin(quote?.Origin)
    ? quote?.RelationDoc?.find(
        doc =>
          doc.DocumentTypeId?.toUpperCase() ===
            documentTypeId.ODV.toUpperCase() ||
          doc.DocumentNumber?.includes('ODV')
      )
    : undefined

const toArray = value =>
  Array.isArray(value) ? value : value == null ? [] : [value]

const selectedEntries = entries =>
  toArray(entries).filter(
    entry =>
      (typeof entry !== 'object' || Object.keys(entry || {}).length > 0) &&
      !hasPendingOrEmptyPaymentMethod(entry)
  )

// A nested PaymentMethod entry is keyed by method name, e.g.
// { CreditoComercial: { PaymentTypeId, Amount } }.
const isNestedPaymentEntry = ([name, payment]) =>
  name !== 'PaymentMethodDetails' && payment && typeof payment === 'object'

const selectedMethodEntries = methods =>
  toArray(methods).flatMap(method =>
    Object.entries(method || {}).filter(
      ([name, payment]) =>
        isNestedPaymentEntry([name, payment]) &&
        !hasPendingOrEmptyPaymentMethod({ [name]: payment })
    )
  )

const readablePaymentName = (...candidates) =>
  candidates.find(value => typeof value === 'string' && value.trim())

// Use another identifier when the payment record has no name. Only for
// display: the quote keeps the order's payments as they came.
const toDisplayPayment = payment =>
  typeof payment !== 'object'
    ? { PaymentName: String(payment) }
    : {
        ...payment,
        PaymentName: readablePaymentName(
          payment.PaymentName,
          payment.PaymentTypeId,
          payment.PaymentMethodDetails?.PaymentTypeId,
          payment.PaymentMethodId,
          payment.PaymentMethodDetails?.PaymentMethodId
        ),
        PaymentAmount:
          payment.PaymentAmount ?? payment.Amount ?? payment.PaidAmount,
      }

const getOrderPayments = order => {
  const payments = selectedEntries(order?.Payments)
  if (payments.length) return payments

  const flatMethods = selectedEntries(order?.PaymentMethod).filter(
    payment =>
      typeof payment !== 'object' ||
      !Object.entries(payment).some(isNestedPaymentEntry)
  )
  if (flatMethods.length) return flatMethods

  return selectedMethodEntries(order?.PaymentMethod).map(([name, payment]) => ({
    PaymentName: readablePaymentName(payment.PaymentName, name),
    PaymentAmount: payment.Amount ?? payment.PaidAmount,
    PaymentMethodDetails: payment,
  }))
}

export const toDisplayPayments = payments =>
  toArray(payments).map(toDisplayPayment)

export const hasAssignedOrderPayment = order =>
  isWebDocumentOrigin(order?.Origin) && getOrderPayments(order).length > 0

export const applyOrderPaymentToQuote = (quote, order) => {
  if (!hasAssignedOrderPayment(order)) return quote

  const payments = getOrderPayments(order)
  const selectedMethods = selectedMethodEntries(order.PaymentMethod)
  const paymentMethod = Array.isArray(order.PaymentMethod)
    ? selectedMethods.length
      ? selectedMethods.map(([name, payment]) => ({ [name]: payment }))
      : selectedEntries(order.PaymentMethod)
    : order.PaymentMethod ?? null
  const creditPayment = toDisplayPayments(payments).find(payment =>
    payment.PaymentName?.toUpperCase().includes('COMERCIAL')
  )
  const creditNumber =
    order.CreditAccountNumber ??
    creditPayment?.PaymentMethodDetails?.PaymentReferenceNumber ??
    creditPayment?.PaymentReferenceNumber ??
    null
  const creditAccountId =
    order.CreditAccountId ??
    creditPayment?.PaymentMethodDetails?.CreditAccountId ??
    creditPayment?.CreditAccountId ??
    (creditNumber && quote.CreditAccountNumber === creditNumber
      ? quote.CreditAccountId
      : null)

  return {
    ...quote,
    Payments: payments,
    PaymentMethod: paymentMethod,
    CreditAccountId: creditAccountId,
    CreditAccountNumber: creditNumber,
    PaymentTotal: order.PaymentTotal ?? order.TotalPayment ?? null,
    TotalPayment: order.TotalPayment ?? order.PaymentTotal ?? null,
    PaymentStatus: order.PaymentStatus ?? order.StatusPayment ?? null,
    StatusPayment: order.StatusPayment ?? order.PaymentStatus ?? null,
    PaymentAmount: order.PaymentAmount ?? null,
    PaymentAmounPay: order.PaymentAmounPay ?? null,
    PaymentDate: order.PaymentDate ?? null,
    PaymentUrl: order.PaymentUrl ?? null,
  }
}
